/**
 * Archived Agreements Tab
 *
 * Admin-only view for viewing archived (deleted) score agreements.
 * Shows who deleted the agreement, when, and allows viewing details.
 */

import { useState } from 'react';
import { useArchivedAgreements } from '../../agreements/useAgreements';
import type { ScoreAgreement } from '../../../lib/evaluationsApi';

// Format date for display
function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Status badge colors
const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  concept: { bg: 'bg-gray-100', text: 'text-gray-800' },
  definitief: { bg: 'bg-blue-100', text: 'text-blue-800' },
  afgerond: { bg: 'bg-green-100', text: 'text-green-800' },
  geannuleerd: { bg: 'bg-red-100', text: 'text-red-800' },
};

const DEFAULT_STATUS_COLOR = { bg: 'bg-gray-100', text: 'text-gray-800' };

// Status display names
const STATUS_NAMES: Record<string, string> = {
  concept: 'Concept',
  definitief: 'Definitief',
  afgerond: 'Afgerond',
  geannuleerd: 'Geannuleerd',
};

// Agreement type metadata
const AGREEMENT_TYPES: Record<string, { icon: string; label: string }> = {
  development: { icon: '📈', label: 'Ontwikkeling' },
  training: { icon: '📚', label: 'Opleiding' },
  target: { icon: '🎯', label: 'Doelstelling' },
  coaching: { icon: '🤝', label: 'Coaching' },
  project: { icon: '📁', label: 'Project' },
  other: { icon: '📝', label: 'Overig' },
};

const DEFAULT_TYPE_INFO = { icon: '📋', label: 'Onbekend' };

// Helper to get type info safely
function getTypeInfo(type: string | undefined): { icon: string; label: string } {
  if (!type) return DEFAULT_TYPE_INFO;
  return AGREEMENT_TYPES[type] || DEFAULT_TYPE_INFO;
}

// Helper to get status color safely
function getStatusColor(status: string): { bg: string; text: string } {
  return STATUS_COLORS[status] || DEFAULT_STATUS_COLOR;
}

interface DetailModalProps {
  agreement: ScoreAgreement;
  onClose: () => void;
}

function DetailModal({ agreement, onClose }: DetailModalProps) {
  const typeInfo = getTypeInfo(agreement.type);
  const statusColor = getStatusColor(agreement.status);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                <h3 className="text-lg font-semibold leading-6 text-gray-900">
                  Verwijderde afspraak
                </h3>
                <div className="mt-4 space-y-3">
                  {/* Type and Status */}
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{typeInfo.icon}</span>
                    <span className="font-medium text-gray-900">{typeInfo.label}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor.bg} ${statusColor.text}`}>
                      {STATUS_NAMES[agreement.status] || agreement.status}
                    </span>
                  </div>

                  {/* Description */}
                  <div>
                    <p className="text-sm font-medium text-gray-500">Beschrijving</p>
                    <p className="mt-1 text-sm text-gray-900">{agreement.beschrijving}</p>
                  </div>

                  {/* Employee */}
                  {agreement.employeeName && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Medewerker</p>
                      <p className="mt-1 text-sm text-gray-900">{agreement.employeeName}</p>
                    </div>
                  )}

                  {/* Deadline */}
                  {agreement.deadline && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Deadline</p>
                      <p className="mt-1 text-sm text-gray-900">{formatDate(agreement.deadline)}</p>
                    </div>
                  )}

                  {/* Archive info */}
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-sm font-medium text-red-600">Verwijderd</p>
                    <div className="mt-1 text-sm text-gray-600 space-y-1">
                      <p>
                        <span className="text-gray-500">Wanneer:</span>{' '}
                        {formatDate(agreement.archivedAt || null)}
                      </p>
                      {agreement.archivedByName && (
                        <p>
                          <span className="text-gray-500">Door:</span>{' '}
                          {agreement.archivedByName}
                        </p>
                      )}
                      {agreement.archivedReason && (
                        <p>
                          <span className="text-gray-500">Reden:</span>{' '}
                          {agreement.archivedReason}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Created info */}
                  <div className="pt-3 border-t border-gray-200 text-xs text-gray-500">
                    <p>Aangemaakt: {formatDate(agreement.createdAt)}</p>
                    {agreement.updatedAt && (
                      <p>Laatste wijziging: {formatDate(agreement.updatedAt)}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              onClick={onClose}
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
            >
              Sluiten
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ArchivedAgreementsTab() {
  const { data, isLoading, error } = useArchivedAgreements();
  const [selectedAgreement, setSelectedAgreement] = useState<ScoreAgreement | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Laden...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-700">
          Fout bij het laden van verwijderde afspraken. Mogelijk heb je geen toegang.
        </p>
      </div>
    );
  }

  const agreements = data?.data || [];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900">Verwijderde Afspraken</h2>
        <p className="text-sm text-gray-500">
          Overzicht van alle gearchiveerde (verwijderde) afspraken
        </p>
      </div>

      {/* Info Banner */}
      <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex">
          <svg className="h-5 w-5 text-amber-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-sm text-amber-700">
            Verwijderde afspraken worden permanent bewaard voor auditdoeleinden. Ze zijn niet meer zichtbaar voor medewerkers en managers.
          </p>
        </div>
      </div>

      {/* Agreements List */}
      {agreements.length > 0 ? (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Afspraak
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Medewerker
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Verwijderd
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Door
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {agreements.map((agreement) => {
                const typeInfo = getTypeInfo(agreement.type);
                const statusColor = getStatusColor(agreement.status);

                return (
                  <tr key={agreement.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{typeInfo.icon}</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                            {agreement.beschrijving}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-500">{typeInfo.label}</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor.bg} ${statusColor.text}`}>
                              {STATUS_NAMES[agreement.status] || agreement.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {agreement.employeeName || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-500">
                        {formatDate(agreement.archivedAt || null)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {agreement.archivedByName || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => setSelectedAgreement(agreement)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Geen verwijderde afspraken</h3>
          <p className="mt-2 text-sm text-gray-500">
            Er zijn nog geen afspraken verwijderd.
          </p>
        </div>
      )}

      {/* Detail Modal */}
      {selectedAgreement && (
        <DetailModal
          agreement={selectedAgreement}
          onClose={() => setSelectedAgreement(null)}
        />
      )}
    </div>
  );
}
